<template>
  <div :class="objectFit === 'cover' ? 'h-full w-full' : ''">
    <img
      v-if="src"
      :src="src"
      :class="
        objectFit === 'cover'
          ? 'h-full w-full object-cover'
          : 'max-h-[inherit] max-w-full object-contain'
      "
      :style="objectPosition ? { objectPosition } : undefined" />
  </div>
</template>

<script lang="ts">
import { HOST, EMU } from '@/globals/typescript/utils/host'
import { getStorageHead } from '@/globals/typescript/utils/images'
import { storage } from '@/firebase/init'
import { getDownloadURL, ref as storageRef } from 'firebase/storage'
import type { IImageBase } from '@team-milva/lab-models/image'
import { cloudinaryTransformations } from '@team-milva/lab-constants/cloudinary'
import type { TCloudinaryTransformation } from '@team-milva/lab-constants/cloudinary'
import type { PropType } from 'vue'

export default {
  name: 'DisplayImage',
  props: {
    image: {
      type: Object as PropType<IImageBase>,
      required: true,
    },
    transformation: {
      type: String as PropType<TCloudinaryTransformation>,
      default: cloudinaryTransformations.DEFAULT,
    },
    // 'cover' fills its parent's box exactly (parent must set its own size)
    // — used for fixed-size square thumbnails. Default 'contain' preserves
    // every existing consumer's current shrink-to-fit behaviour.
    objectFit: {
      type: String as PropType<'contain' | 'cover'>,
      default: 'contain',
    },
    // CSS object-position, e.g. "58% 60%" — lets a cover-fit crop stay
    // centred on a specific point in the source image (a floor-plan marker)
    // rather than the image's geometric centre.
    objectPosition: {
      type: String,
      default: undefined,
    },
  },
  data() {
    return {
      src: '',
      objectURL: null as string | null,

      checkThresholdMs: 1000 * 60 * 10, // 10 minutes

      states: {
        isLoading: true,
        failed: false,
        uploading: false,
        offline: false,
      },
    }
  },
  computed: {
    storagePath() {
      return this.image.path
    },
    cloudinaryPath() {
      return `${HOST.cloudinary}/${this.storagePath}`
    },
  },
  methods: {
    async setSrc() {
      const { image } = this
      if (!image) {
        this.src = ''
        return
      }

      // When image is not uploaded yet, show base64URL or SVG preview if available
      // - Use base64URL if available
      if (image.base64URL) {
        this.src = image.base64URL
        return
      }

      // - Use SVG file if available
      if (image.svgFile) {
        if (this.objectURL) {
          URL.revokeObjectURL(this.objectURL)
        }
        this.objectURL = URL.createObjectURL(image.svgFile)
        this.src = this.objectURL
        return
      }

      // Check upload status when newer image
      const createdAt = this.image?.createdAt || 0
      const now = Date.now()
      const diff = now - createdAt
      if (createdAt && diff < this.checkThresholdMs) {
        try {
          await getStorageHead({ path: this.image.path })
        } catch (_error: Error | unknown) {
          this.states.uploading = true
          this.states.isLoading = false
          return
        }
      }

      // Cloudinary always targets the real per-environment bucket, never the
      // local Storage emulator, so it can never resolve an image that only
      // exists there. Go straight through the Storage SDK instead — it's
      // already connected to the emulator (see firebase/init.ts) whenever
      // this flag is on.
      if (EMU.isEmulatingStorage) {
        try {
          this.src = await getDownloadURL(storageRef(storage, this.image.path))
        } catch (_error: Error | unknown) {
          this.states.failed = true
          this.states.isLoading = false
        }
        return
      }

      // Use image from cloudinary
      this.src = `${this.cloudinaryPath}?${this.getTranformation()}`
    },
    getTranformation() {
      return `tx=${this.transformation}`
    },
  },
  created() {
    this.setSrc()
  },
  beforeUnmount() {
    if (this.objectURL) {
      URL.revokeObjectURL(this.objectURL)
      this.objectURL = null
    }
  },
}
</script>

